import { IdTokenResult } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { useUserState } from '@/state';
import { ownsOrganization } from '@/utils/organizationOwner';

export type Permission = 'all' | 'editor' | 'roomServiceManager' | 'rentalManager' | 'reportManager' | 'spaManager' | 'storeManager';
export type StaffServicePermission = 'reports' | 'roomservice' | 'store' | 'rental' | 'qrscanner' | 'rooms';

interface CustomClaims {
  role?: string[];
  permissions?: Permission[];
  staffService?: boolean;
  /** @deprecated #82 zdejmuje ten claim; sekcje Staff są w users/{uid}.staffSections[orgId]. */
  staffServicePermissions?: StaffServicePermission[];
  /** Flaga personelu nadawana skryptem/backendem (claim `isStaff`). */
  isStaff?: boolean;
  /** Administrator HotelSpot — ten sam warunek co w backendowym `verifyAdminAccess`. */
  isAdmin?: boolean;
  isHotelspotAdmin?: boolean;
  /**
   * Organizacje, których konto jest właścicielem. ACS ustawia TABLICĘ id
   * (konto może mieć kilka obiektów), starsze konta bywają pojedynczym id.
   */
  owner?: string[] | string;
}

/**
 * Przepisanie claimów z tokenu na nasz kształt.
 *
 * JEDNO miejsce, bo odczyt dzieje się dwa razy — przy starcie i przy
 * `refreshClaims`. Wcześniej były to dwie kopie tej samej listy pól i obie
 * rozjechały się z `CustomClaims`: `isAdmin`, `isHotelspotAdmin` i `owner`
 * były zadeklarowane w typie i czytane niżej, ale nigdy nie trafiały do stanu.
 * Efekt: konto właściciela albo administratora HotelSpot nie było w panelu
 * rozpoznawane — mimo poprawnych claimów w tokenie.
 */
const readClaims = (idTokenResult: IdTokenResult): CustomClaims => ({
  role: idTokenResult.claims.role as string[] | undefined,
  permissions: idTokenResult.claims.permissions as Permission[] | undefined,
  staffService: idTokenResult.claims.staffService as boolean | undefined,
  isStaff: idTokenResult.claims.isStaff as boolean | undefined,
  isAdmin: idTokenResult.claims.isAdmin as boolean | undefined,
  isHotelspotAdmin: idTokenResult.claims.isHotelspotAdmin as boolean | undefined,
  owner: idTokenResult.claims.owner as string[] | string | undefined,
});

/**
 * Hook to check user permissions from Firebase custom claims
 * Permissions are stored in custom claims and verified server-side
 *
 * IMPORTANT: This requires the user to refresh their ID token after permissions change
 * Custom claims are read from the Firebase Auth token
 */
export const usePermissions = () => {
  const { currentUser } = useUserState();
  const [customClaims, setCustomClaims] = useState<CustomClaims>({});
  // Claimy czytamy asynchronicznie z tokenu. Widok, który na ich podstawie
  // przekierowuje (np. DeleteOrganization), musi poczekać na pierwszy odczyt,
  // inaczej pusty stan startowy odsyła administratora zanim claimy dotrą.
  const [claimsLoaded, setClaimsLoaded] = useState(false);

  useEffect(() => {
    // Nowe konto = claimy poprzedniego są nieaktualne; do pierwszego odczytu
    // widoki z przekierowaniem mają czekać, a nie decydować na starych danych.
    setClaimsLoaded(false);
    setCustomClaims({});
    const fetchClaims = async () => {
      if (!currentUser) {
        setCustomClaims({});
        setClaimsLoaded(true);
        return;
      }

      try {
        const idTokenResult = await currentUser.getIdTokenResult();
        setCustomClaims(readClaims(idTokenResult));
      } catch (error) {
        setCustomClaims({});
      }
      setClaimsLoaded(true);
    };

    fetchClaims();

    // Refresh claims every 5 minutes or when user changes
    const interval = setInterval(fetchClaims, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const permissions = customClaims.permissions || [];
  const roles = customClaims.role || [];
  const staffService = customClaims.staffService || false;

  const hasPermission = (permission: Permission): boolean => {
    if (permissions.includes('all')) {
      return true;
    }

    return permissions.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions: Permission[]): boolean => requiredPermissions.some((permission) => hasPermission(permission));

  const hasAllPermissions = (requiredPermissions: Permission[]): boolean => requiredPermissions.every((permission) => hasPermission(permission));

  // Właściciel KONKRETNEJ organizacji według claimu `owner` (id z ACS). Globalna
  // rola `owner` od #81 nie oznacza właściciela, bo dostaje ją każde konto z
  // logowania Google. Pełny warunek (z `organizations/{id}.owner`) składają
  // wywołujący albo `isOrganizationOwner` z `@/utils/organizationOwner`.
  const isOwnerOf = (organizationId?: string | null): boolean => ownsOrganization(customClaims.owner, organizationId);

  return {
    permissions,
    roles,
    staffService,
    isStaff: customClaims.isStaff === true,
    // Administrator HotelSpot — ten sam warunek, co w backendzie
    // (`verifyAdminAccess`): claim `isAdmin` albo `isHotelspotAdmin`.
    isAdmin: customClaims.isAdmin === true || customClaims.isHotelspotAdmin === true,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    /** Właściciel KONKRETNEJ organizacji — po id z `organizationState`. */
    isOwnerOf,
    /** Surowy claim `owner` (tablica id albo jedno id), bez globalnej roli `owner`. */
    ownerClaim: customClaims.owner,
    claimsLoaded,
    refreshClaims: async () => {
      if (currentUser) {
        await currentUser.getIdToken(true); // Force refresh
        const idTokenResult = await currentUser.getIdTokenResult();
        setCustomClaims(readClaims(idTokenResult));
      }
    },
  };
};
